import type { CSSProperties, FC, ReactNode, Ref } from 'react'

import { useImperativeHandle, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { pad } from '@/lib/format'
import { prefersReducedMotion } from '@/lib/motion'
import { siteConfig } from '@/constants/site'

export interface CurtainMeta {
    label: string
    index: string
}

export interface CurtainHandle {
    intro: (onReveal: () => void) => Promise<void>
    cover: (meta: CurtainMeta) => Promise<void>
    reveal: (onReveal: () => void) => Promise<void>
}

interface CurtainProps {
    ref?: Ref<CurtainHandle>
}

const INTRO_WORD = ['E', 'L', 'I', 'O']
const COORDINATES = '52.5200° N — 13.4050° E'

const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const splitToHtml = (text: string): string => text
    .split(' ')
    .map(word => `<span class="inline-block whitespace-nowrap">${Array.from(word).map(char => `<span class="mask"><span data-char>${escapeHtml(char)}</span></span>`).join('')}</span>`)
    .join(' ')

const pageElement = (): HTMLElement | null => document.querySelector<HTMLElement>('[data-page]')

const Curtain: FC<CurtainProps> = ({ ref }): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const acid = useRef<HTMLDivElement>(null)
    const panel = useRef<HTMLDivElement>(null)
    const introWord = useRef<HTMLDivElement>(null)
    const title = useRef<HTMLDivElement>(null)
    const topNote = useRef<HTMLSpanElement>(null)
    const index = useRef<HTMLSpanElement>(null)
    const bottomNote = useRef<HTMLSpanElement>(null)
    const counter = useRef<HTMLSpanElement>(null)
    const titleTween = useRef<gsap.core.Tween | null>(null)

    useImperativeHandle(ref, () => ({
        intro: (onReveal: () => void): Promise<void> => new Promise<void>(resolve => {
            const rootEl = root.current
            const chars = introWord.current?.querySelectorAll('[data-char]')

            if (!rootEl || !chars) {
                onReveal()
                resolve()

                return
            }

            rootEl.dataset.state = 'running'

            if (prefersReducedMotion()) {
                onReveal()
                gsap.set([panel.current, acid.current], { yPercent: -100 })
                gsap.set(rootEl, { visibility: 'hidden' })
                rootEl.dataset.state = 'idle'
                resolve()

                return
            }

            const count = { value: 0 }
            const page = pageElement()

            const timeline = gsap.timeline({
                onComplete: () => {
                    gsap.set(rootEl, { visibility: 'hidden' })
                    rootEl.dataset.state = 'idle'
                    resolve()
                }
            })

            timeline
                .fromTo(chars, { y: 0, yPercent: 140 }, { yPercent: 0, duration: 1.1, stagger: 0.07, ease: 'expo.out' }, 0.05)
                .to(count, {
                    value: 100,
                    duration: 1.1,
                    ease: 'power3.inOut',
                    onUpdate: () => {
                        if (counter.current) counter.current.textContent = pad(Math.round(count.value), 3)
                    }
                }, 0)
                .to(chars, { yPercent: -140, duration: 0.5, stagger: 0.04, ease: 'expo.in' }, 1.15)
                .call(onReveal, [], 1.5)
                .to(panel.current, { yPercent: -100, duration: 1, ease: 'expo.inOut' }, 1.42)
                .to(acid.current, { yPercent: -100, duration: 1, ease: 'expo.inOut' }, 1.52)

            if (page) timeline.fromTo(page, { y: window.innerHeight * 0.14 }, { y: 0, duration: 1.4, ease: 'expo.out', clearProps: 'transform', immediateRender: false }, 1.62)
        }),

        cover: (meta: CurtainMeta): Promise<void> => new Promise<void>(resolve => {
            const rootEl = root.current
            const titleEl = title.current

            if (!rootEl || !titleEl) {
                resolve()

                return
            }

            rootEl.dataset.state = 'running'

            if (topNote.current) topNote.current.textContent = '(Elio) — Now entering'
            if (index.current) index.current.textContent = `(${meta.index})`
            if (bottomNote.current) bottomNote.current.textContent = siteConfig.season
            if (counter.current) counter.current.textContent = COORDINATES

            titleEl.innerHTML = splitToHtml(meta.label)

            const chars = titleEl.querySelectorAll('[data-char]')

            gsap.set(introWord.current, { autoAlpha: 0 })
            gsap.set(titleEl, { autoAlpha: 1 })
            gsap.set(rootEl, { visibility: 'visible', autoAlpha: 1 })

            if (prefersReducedMotion()) {
                gsap.set([acid.current, panel.current], { yPercent: 0 })
                gsap.set(chars, { yPercent: 0 })
                resolve()

                return
            }

            gsap.set([acid.current, panel.current], { yPercent: 100 })
            gsap.set(chars, { yPercent: 140 })

            const page = pageElement()
            const timeline = gsap.timeline()

            timeline
                .to(acid.current, { yPercent: 0, duration: 0.78, ease: 'expo.inOut' }, 0)
                .to(panel.current, { yPercent: 0, duration: 0.78, ease: 'expo.inOut' }, 0.07)
                .call(resolve, [], 0.86)

            if (page) timeline.to(page, { y: -window.innerHeight * 0.1, duration: 0.85, ease: 'expo.inOut' }, 0)

            titleTween.current = gsap.to(chars, { yPercent: 0, duration: 0.75, stagger: 0.03, ease: 'expo.out', delay: 0.38 })
        }),

        reveal: (onReveal: () => void): Promise<void> => new Promise<void>(resolve => {
            const rootEl = root.current

            if (!rootEl) {
                onReveal()
                resolve()

                return
            }

            const chars = title.current?.querySelectorAll('[data-char]') ?? []

            const done = (): void => {
                gsap.set(rootEl, { visibility: 'hidden' })
                rootEl.dataset.state = 'idle'
                resolve()
            }

            if (prefersReducedMotion()) {
                onReveal()
                gsap.to(rootEl, {
                    autoAlpha: 0,
                    duration: 0.3,
                    ease: 'none',
                    onComplete: () => {
                        gsap.set([panel.current, acid.current], { yPercent: -100 })
                        done()
                    }
                })

                return
            }

            titleTween.current?.kill()

            const page = pageElement()
            const timeline = gsap.timeline({ onComplete: done })

            timeline
                .to(chars, { yPercent: -140, duration: 0.5, stagger: 0.02, ease: 'expo.in', overwrite: true }, 0)
                .call(onReveal, [], 0.32)
                .to(panel.current, { yPercent: -100, duration: 1, ease: 'expo.inOut' }, 0.26)
                .to(acid.current, { yPercent: -100, duration: 1, ease: 'expo.inOut' }, 0.34)

            if (page) timeline.fromTo(page, { y: window.innerHeight * 0.14 }, { y: 0, duration: 1.3, ease: 'expo.out', clearProps: 'transform', immediateRender: false }, 0.48)
        })
    }), [])

    return (
        <div
            ref={root}
            data-state='intro'
            aria-hidden='true'
            className='curtain pointer-events-none fixed inset-0 z-[80]'
        >
            <div ref={acid} className='absolute inset-0 bg-acid' />

            <div
                ref={panel}
                className='absolute inset-0 flex flex-col justify-between bg-bone py-6 text-ink gutter md:py-8'
            >
                <div className='flex items-start justify-between gap-6 label-mono'>
                    <span ref={topNote}>
                        (SS26) — Essentials 04
                    </span>

                    <span ref={index}>
                        (00)
                    </span>
                </div>

                <div className='relative'>
                    <div
                        ref={introWord}
                        className='flex justify-center font-heading text-[34vw] leading-[0.8] tracking-[-0.02em] md:text-[22vw]'
                    >
                        {INTRO_WORD.map(letter => (
                            <span key={letter} className='mask'>
                                <span data-char style={{ transform: 'translateY(140%)' } as CSSProperties}>
                                    {letter}
                                </span>
                            </span>
                        ))}
                    </div>

                    <div
                        ref={title}
                        className='invisible absolute inset-x-0 bottom-0 display-xl uppercase'
                    />
                </div>

                <div className='flex items-end justify-between gap-6 label-mono'>
                    <span ref={bottomNote}>
                        Designed for the unordinary
                    </span>

                    <span ref={counter} className='tabular-nums'>
                        000
                    </span>
                </div>
            </div>
        </div>
    )
}

export default Curtain
